import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Target, Clock, ArrowRight, CheckCircle, Settings, Database, Zap, Layers } from "lucide-react";

export const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D4A6D]/5 border border-[#2D4A6D]/10 mb-8">
                <span className="w-2 h-2 bg-[#2D4A6D] rounded-full" />
                <span className="text-sm font-medium text-[#2D4A6D] tracking-wide uppercase">Done By Emma</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#2D4A6D] mb-4 leading-[1.1] font-serif">
                Operations<br />
                Systems<br />
                CRM<br />
                <span className="font-semibold">Automation</span>
              </h1>

              <p className="text-lg text-slate-700 font-medium leading-relaxed mb-4">
                Structured operations. Connected systems. Efficient workflows.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-10">
                Operational infrastructure designed to reduce friction, organize complexity, and keep execution moving.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <button data-testid="hero-explore-btn" className="bg-[#2D4A6D] hover:bg-[#1F3A5F] text-white px-8 py-4 text-base font-medium flex items-center justify-center w-full sm:w-auto">
                    Explore Services <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </Link>
                <Link to="/contact">
                  <button data-testid="hero-contact-btn" className="border-2 border-[#2D4A6D] text-[#2D4A6D] hover:bg-[#2D4A6D] hover:text-white px-8 py-4 text-base font-medium w-full sm:w-auto transition-colors">
                    Start a Conversation
                  </button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#2D4A6D]/10" />
                <img 
                  src="https://images.pexels.com/photos/5240018/pexels-photo-5240018.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Professional workspace"
                  className="w-full h-auto relative z-10"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Done By Emma Standard */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl text-[#2D4A6D] mb-4 font-serif">The Done By Emma Standard</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "No Chasing Updates", desc: "Proactive communication and consistent follow-through on every commitment." },
              { icon: Target, title: "No Repeating Instructions", desc: "Systems designed to capture preferences, processes, and expectations." },
              { icon: Clock, title: "No Reactive Operations", desc: "Structured workflows designed to keep priorities visible and execution moving." }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="h-full p-8 border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors bg-white">
                  <div className="w-12 h-12 bg-[#2D4A6D] flex items-center justify-center mb-6">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#2D4A6D] mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Capabilities */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl text-[#2D4A6D] mb-4 font-serif">Operational Services</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Settings, name: "Operations", tagline: "Structure behind the day-to-day.", desc: "Process organization, administrative workflows, coordination, documentation, task management, research, and operational execution." },
              { icon: Layers, name: "Systems", tagline: "Connected systems. Organized information.", desc: "Process mapping, workflow design, system organization, tool implementation, integrations, and operational infrastructure." },
              { icon: Database, name: "CRM", tagline: "From data to actionable pipelines.", desc: "CRM organization, database management, pipeline management, lead tracking, follow-up systems, and workflow configuration." },
              { icon: Zap, name: "Automation", tagline: "Less repetition. Better workflows.", desc: "Workflow automation, integrations, triggers, notifications, task automation, and repetitive-process reduction." }
            ].map((cap, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="h-full p-6 border border-slate-200 hover:border-[#2D4A6D]/50 transition-colors bg-white">
                  <div className="w-12 h-12 bg-[#2D4A6D] flex items-center justify-center mb-5">
                    <cap.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-[#2D4A6D] mb-1">{cap.name}</h3>
                  <p className="text-sm text-[#2D4A6D]/70 font-medium mb-3">{cap.tagline}</p>
                  <p className="text-sm text-slate-600">{cap.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <button className="border-2 border-[#2D4A6D] text-[#2D4A6D] hover:bg-[#2D4A6D] hover:text-white px-8 py-3 font-medium transition-colors flex items-center mx-auto">
                View All Services <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* The Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl text-[#2D4A6D] mb-4 font-serif">The Approach</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-8" />
            <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Every operation is different. Processes, systems, and workflows should be structured around the way a business actually operates — not forced into a predefined model.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { step: "01", title: "Understand" },
              { step: "02", title: "Organize" },
              { step: "03", title: "Optimize" },
              { step: "04", title: "Automate" },
              { step: "05", title: "Execute" }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="text-center p-6 border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors bg-white h-full">
                  <div className="text-2xl font-light text-[#2D4A6D]/40 mb-2">{item.step}</div>
                  <h4 className="text-base font-semibold text-[#2D4A6D]">{item.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-600 mt-10 max-w-2xl mx-auto leading-relaxed">
            The focus is simple: understand the existing operation, identify opportunities for improvement, establish structure, and implement practical systems that make the workflow more efficient.
          </p>
        </div>
      </section>

      {/* Systems & Technology */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl text-[#2D4A6D] mb-4 font-serif">Systems & Technology</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A flexible technology stack supporting CRM, workflow management, automation, communication, documentation, and business operations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { category: "CRM", tools: ["Salesforce", "HubSpot", "Zoho", "GoHighLevel", "Podio", "Forefront", "REISift", "REI Reply"] },
              { category: "Automation", tools: ["Zapier", "Make"] },
              { category: "Project & Workflow", tools: ["ClickUp", "Notion", "Trello", "Asana", "Monday.com", "Airtable"] },
              { category: "Communication", tools: ["Slack", "Zoom", "Google Meet", "Discord", "Skype"] },
              { category: "Productivity & Documents", tools: ["Google Workspace", "Microsoft Office", "Google Calendar", "Microsoft Outlook", "Dropbox", "OneDrive", "DocuSign"] },
              { category: "AI & Creative", tools: ["ChatGPT", "Claude", "Gemini", "Copilot", "Grammarly", "Canva"] }
            ].map((group, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <div className="h-full p-6 bg-white border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors">
                  <h4 className="text-sm font-semibold text-[#2D4A6D] uppercase tracking-wider mb-4">{group.category}</h4>
                  <div className="flex flex-wrap gap-2">
                    {group.tools.map((tool, j) => (
                      <span key={j} className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-1">{tool}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Operate Differently? CTA */}
      <section className="py-24 bg-[#2D4A6D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl mb-8 font-serif">Ready to Operate Differently?</h2>
          <div className="w-16 h-0.5 bg-white/30 mx-auto mb-10" />

          <p className="text-slate-300 mb-4">
            When operational complexity begins taking attention away from higher-value work, the right systems can change the way the business operates.
          </p>
          <p className="text-lg text-white font-medium mb-10">
            During a strategy call, the focus is to:
          </p>

          <div className="bg-white/10 p-8 border border-white/20 mb-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              {["Identify the highest-leverage operational gaps", "Define ownership boundaries", "Clarify system and workflow requirements", "Determine the appropriate engagement structure"].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <Link to="/book">
            <button className="bg-white text-[#2D4A6D] hover:bg-slate-100 px-10 py-4 text-base font-medium flex items-center mx-auto">
              Book a Strategy Call <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
